import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CheckCircle, Spinner, XCircle, ShoppingBag, ArrowRight } from '@phosphor-icons/react';
import Navbar from '@/components/custom/Navbar';
import Footer from '@/components/custom/Footer';
import { useCart } from '@/context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { fetchCart } = useCart();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    const pollPaymentStatus = async (attempts = 0) => {
      if (!sessionId || attempts >= 5) {
        if (attempts >= 5) setStatus('error');
        return;
      }

      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        setPaymentData(response.data);

        if (response.data.payment_status === 'paid') {
          setStatus('success');
          fetchCart(); // Refresh cart to show empty
        } else if (response.data.status === 'expired') {
          setStatus('error');
        } else {
          // Continue polling
          setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (attempts < 4) {
          setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, fetchCart]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24 pb-24">
        <div className="max-w-lg mx-auto px-6">
          <Card className="p-8 text-center" data-testid="checkout-result">
            {status === 'loading' && (
              <>
                <Spinner weight="bold" className="w-16 h-16 mx-auto text-primary animate-spin mb-6" />
                <h1 className="text-2xl font-medium mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Processing Payment
                </h1>
                <p className="text-muted-foreground">
                  Please wait while we confirm your order...
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-20 h-20 mx-auto bg-[#4A6C4C]/10 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle weight="duotone" className="w-12 h-12 text-[#4A6C4C]" />
                </div>
                <h1 className="text-2xl font-medium mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Order Confirmed!
                </h1>
                <p className="text-muted-foreground mb-6">
                  Thank you for supporting sustainable artisan communities. 
                  Your order has been placed successfully.
                </p>
                {paymentData && (
                  <div className="bg-secondary/50 rounded-lg p-4 mb-6 text-left">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-muted-foreground">Amount Paid</span>
                      <span className="font-medium">${paymentData.amount?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Status</span>
                      <span className="text-[#4A6C4C] font-medium capitalize">{paymentData.payment_status}</span>
                    </div>
                  </div>
                )}
                <div className="flex flex-col gap-3">
                  <Link to="/products">
                    <Button className="w-full rounded-full gap-2">
                      <ShoppingBag weight="bold" className="w-4 h-4" />
                      Continue Shopping
                    </Button>
                  </Link>
                  <Link to="/impact">
                    <Button variant="outline" className="w-full rounded-full gap-2">
                      View Your Impact
                      <ArrowRight weight="bold" className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="w-20 h-20 mx-auto bg-destructive/10 rounded-full flex items-center justify-center mb-6">
                  <XCircle weight="duotone" className="w-12 h-12 text-destructive" />
                </div>
                <h1 className="text-2xl font-medium mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Payment Issue
                </h1>
                <p className="text-muted-foreground mb-6">
                  We couldn't confirm your payment. Please check your email for confirmation 
                  or try again.
                </p>
                <div className="flex flex-col gap-3">
                  <Link to="/cart">
                    <Button className="w-full rounded-full">
                      Return to Cart
                    </Button>
                  </Link>
                  <Link to="/">
                    <Button variant="outline" className="w-full rounded-full">
                      Go to Home
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CheckoutSuccessPage;
